import React, { Fragment } from 'react'
import { connect } from 'react-redux'
import { useNavigate, Navigate } from 'react-router-dom'

// utils
import {
  getLocalStorageValues,
  fetchCommanderToken,
  removeCommanderToken
} from 'utils/localStorageManager'
import isLoggedIn from 'utils/auth'
import logos from 'utils/logos'

// redux
import * as authModule from '../../redux/modules/authentication'

// assets
const userIcon = 'https://cdn.browniepoints.africa/assets/bpimg_hkk3to4hxef989p.png'

// styles
import {
  MainNavWrapper,
  MainNav,
  DashboardNavLeft,
  NavRight,
  FlatNavLink,
  LogoLink,
  SmallLogo,
  DashboardLogo,
  FullLogo,
  NavOutterWrapper,
  NavInnerWrapper,
  AccountMenuContainer,
  AvatarWrapper,
  Avatar,
  ArrowDownIcon,
  ArrowUpIcon,
  Username,
  AccountMenu,
  MenuNavLink,
  Clickable
} from './styles'

// Load components synchronously
import ConditionalDisplay from 'components/ConditionalDisplay/ConditionalDisplay'
import Link from 'components/Link'
import CookieHeader from 'components/CookieHeader'
import FlashMessage from 'components/FlashMessage'
import LinkButton from 'components/SmallButtonPrimary/LinkButton'

const { smallLogoPath, smallAltLogoPath, fullAltLogoPath } = logos

const HeaderDropdownArea = ({
  displayName,
  fallbackIcon,
  hasAvatar,
  headerAvatar,
  isOpen,
  toggleHeaderDropdown
}) => {
  return (
    <Clickable onClick={toggleHeaderDropdown}>
      <AvatarWrapper>
        <Avatar src={headerAvatar || fallbackIcon} hasAvatar={hasAvatar} alt="User avatar" />
      </AvatarWrapper>

      <Username>{displayName}</Username>

      {isOpen ? <ArrowUpIcon /> : <ArrowDownIcon />}
    </Clickable>
  )
}

function withNavigation(Component) {
  return function NavigationWrappedComponent(props) {
    const navigate = useNavigate()
    return <Component {...props} navigate={navigate} />
  }
}

class Header extends React.Component {
  constructor(props) {
    super(props)

    this.state = { isOpen: false }
    this.setWrapperRef = this.setWrapperRef.bind(this)
    this.handleClickOutside = this.handleClickOutside.bind(this)
    this.toggleHeaderDropdown = this.toggleHeaderDropdown.bind(this)
  }

  setWrapperRef(node) {
    this.wrapperRef = node
  }

  handleClickOutside(event) {
    if (this.state.isOpen && this.wrapperRef && !this.wrapperRef.contains(event.target)) {
      this.setState(state => ({ ...state, isOpen: false }))
    }
  }

  componentDidMount() {
    document.addEventListener('click', this.handleClickOutside)

    if (isLoggedIn()) {
      this.props.fetchMyRoles()
    }
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
  }

  toggleHeaderDropdown() {
    this.setState(state => ({ ...state, isOpen: !this.state.isOpen }))
  }

  render() {
    const { isOpen } = this.state
    const user = getLocalStorageValues()

    const { loggedIn, logout, dashboard, searchPage, isLoading, userData } = this.props

    if (userData && userData.on_probation) {
      return <Navigate to={`/probation`} replace />
    }

    const headerAvatar = user.avatar_url || userIcon
    const hasAvatar = !!user.avatar_url
    const displayName = user.f_name

    const hasCommandeeredToken = fetchCommanderToken()
    if (hasCommandeeredToken) {
      return (
        <Fragment>
          <MainNavWrapper>
            <MainNav hasCommandeeredToken isLoading={isLoading} ref={this.setWrapperRef} isFixed>
              <NavOutterWrapper isFixed>
                <NavInnerWrapper>
                  <DashboardNavLeft>
                    <Link to="/">
                      <DashboardLogo isFixed src={smallLogoPath} alt="Growsmart Logo" />
                    </Link>
                  </DashboardNavLeft>

                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <LinkButton onClick={() => removeCommanderToken()}>Eject</LinkButton>
                  </div>
                </NavInnerWrapper>
              </NavOutterWrapper>
            </MainNav>
          </MainNavWrapper>
        </Fragment>
      )
    }

    return (
      <Fragment>
        <MainNavWrapper>
          <MainNav
            hasCommandeeredToken={hasCommandeeredToken}
            isLoading={isLoading}
            ref={this.setWrapperRef}
            isFixed={dashboard}
          >
            <NavOutterWrapper isFixed={dashboard}>
              <NavInnerWrapper>
                <ConditionalDisplay displayWhen={[dashboard]}>
                  <DashboardNavLeft>
                    <Link to="/">
                      <DashboardLogo isFixed={dashboard} src={smallLogoPath} alt="Growsmart Logo" />
                    </Link>
                  </DashboardNavLeft>
                </ConditionalDisplay>

                <ConditionalDisplay displayWhen={[!dashboard]}>
                  <LogoLink isGs searchPage={searchPage} href="/">
                    <FullLogo src={fullAltLogoPath} alt="Growsmart Logo" />
                    <SmallLogo src={smallAltLogoPath} alt="Growsmart Logo" />
                  </LogoLink>
                </ConditionalDisplay>

                <ConditionalDisplay displayWhen={[loggedIn]}>
                  <AccountMenuContainer>
                    <HeaderDropdownArea
                      toggleHeaderDropdown={this.toggleHeaderDropdown}
                      isOpen={isOpen}
                      headerAvatar={headerAvatar}
                      hasAvatar={hasAvatar}
                      displayName={displayName}
                      fallbackIcon={userIcon}
                    />

                    <ConditionalDisplay displayWhen={[isOpen]}>
                      <AccountMenu>
                        <MenuNavLink href={'/dashboard'}>View Dashboard</MenuNavLink>

                        <MenuNavLink href={`/dashboard/account`}>Account Settings</MenuNavLink>

                        <Clickable InMenu onClick={logout}>
                          Log out
                        </Clickable>
                      </AccountMenu>
                    </ConditionalDisplay>
                  </AccountMenuContainer>
                </ConditionalDisplay>

                <ConditionalDisplay displayWhen={[!loggedIn]}>
                  <NavRight>
                    <FlatNavLink padright href={'/login'}>
                      Log in
                    </FlatNavLink>
                  </NavRight>
                </ConditionalDisplay>
              </NavInnerWrapper>
            </NavOutterWrapper>
          </MainNav>

          <CookieHeader />
          <FlashMessage />
        </MainNavWrapper>
      </Fragment>
    )
  }
}

const mapStateToProps = state => ({
  isLoading: state.authentication.isLoading,
  errorMessage: state.authentication.errorMessage,
  userData: state.authentication.userData
})

const mapDispatchToProps = dispatch => ({
  fetchMyRoles: () => dispatch(authModule.fetchMyRoles())
})

export default connect(mapStateToProps, mapDispatchToProps)(withNavigation(Header))
